<template>
  <div class="page-container">
    <md-app md-waterfall class="app-container">
      <md-app-toolbar class="md-primary">
        <div class="container dn-row">
          <router-link class="logo-wrapper" :to="{ name: 'home', params: { siteKey }}">
            <img v-if="logoUrl !== ''" :src="logoUrl" class="logo" />
          </router-link>
          <div class="app-title">
            <small v-if="!notFound">Send Message To:</small>
            <div class="md-title">{{ displayName }}</div>
          </div>
          <div class="filler" />
        </div>
      </md-app-toolbar>

      <md-app-content>
        <!-- Sending Display -->
        <div v-if="sending" class="loading container center-container pad-lg">
          <div class="dn-row">
            <md-progress-spinner :md-diameter="26" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
            <span>Sending...</span>
            <div class="filler" />
          </div>
        </div>

        <!-- Sent Message -->
        <div v-if="sent && !errorSending" class="sent container center-container pad-lg">
          <div class="text-center">
            <div class="md-title">Thank you!</div>
            <div>Your message has been sent.</div>
            <div>
              <a @click="navigateHome()" class="home-link">Send another message</a>
            </div>
          </div>
        </div>

        <!-- Not Found Message -->
        <div v-if="notFound" class="sent container center-container pad-lg">
          <div class="text-center">
            <div class="md-title">Provider Not Found</div>
            <div>Please try again.</div>
            <div>
              <router-link :to="{ name: 'home', params: { siteKey }}">Return Home</router-link>
            </div>
          </div>
        </div>

        <!-- Error Sending Message -->
        <div v-if="errorSending" class="sent container center-container pad-lg">
          <div class="text-center">
            <div class="md-title">We're sorry, but we were unable to deliver your message.</div>
            <div>Please try again later.</div>
            <div>
              <a @click="navigateHome()" class="home-link">Send another message</a>
            </div>
          </div>
        </div>

        <!-- Too Many Requests Error -->
        <div v-if="tooManyRequestsError" class="sent container center-container pad-lg">
          <div class="text-center">
            <div class="md-title">Too many requests</div>
            <div>Please try again later.</div>
          </div>
        </div>

        <!-- Form Display -->
        <div v-if="!sending && !sent && !notFound && !errorSending && !tooManyRequestsError" class="container pad-lg">
          <div class="form">
            <md-field :class="getValidationClass('name')">
              <label>Your Name</label>
              <md-input required name="name" v-model.trim="form.name" @change="touch('name')"></md-input>
              <span class="md-error">Required, max length 100 characters</span>
            </md-field>
            <md-field :class="getValidationClass('phone')">
              <label>Callback Number</label>
              <md-input required name="phone" type="text" v-model.trim="form.phone" @change="touch('phone')"></md-input>
              <span class="md-error">Required, must be a 10 digit phone number</span>
            </md-field>
            <md-field :class="getValidationClass('message')">
              <label>Message</label>
              <md-textarea @change="touch('message')" class="message-textarea" required name="message" v-model.trim="form.message" md-autogrow maxlength="1000"></md-textarea>
              <span class="md-error">Required</span>
            </md-field>
            <div class="dn-row">
              <div class="captcha" @click="recaptcha">
                <input type="checkbox" @click="recaptcha" :checked="notABot" />I'm not a bot
              </div>
              <div class="filler" />
              <md-button class="md-raised md-primary" :disabled="!notABot" @click="submit">Send</md-button>
            </div>
            <div v-if="errorSending" class="dn-row error-sending">
              <span class="md-error">There was an error sending your message. Please try again.</span>
            </div>
          </div>
        </div>
      </md-app-content>
    </md-app>
  </div>
</template>

<script>
import _ from 'lodash';
import { required, maxLength } from 'vuelidate/lib/validators';

const faviconUrl = '/c/favicon.png';

export const nonAlphabetic = str => {
  return /^[^A-Za-z]+$/.test(str);
}

export const tenDigits = str => {
  return str.replace(/[\W_]+/g, '').length === 10;
}

export default {
  name: 'message-form',
  props: {
    siteKey: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    form: {
      name: null,
      message: null,
      phone: null
    },
    sending: false,
    sent: false,
    recaptchaToken: '',
    logoUrl: '',
    displayName: '',
    targetId: 0,
    targetType: '',
    aliasId: 0,
    notFound: false,
    errorSending: false,
    fromRoute: null,
    tooManyRequestsError: false,
  }),
  metaInfo() {
    return {
      link: [
        { rel: 'icon', href: this.logoUrl || faviconUrl }
      ]
    }
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(100)
      },
      message: {
        required
      },
      phone: {
        required,
        nonAlphabetic,
        tenDigits
      }
    }
  },
  computed: {
    notABot() {
      return this.recaptchaToken !== '';
    },
  },
  methods: {
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName];

      if (typeof field !== 'undefined') {
        if(field.$invalid && field.$dirty) { return 'md-invalid'; }
      }

      return '';
    },
    touch(fieldName) {
      const field = this.$v.form[fieldName];

      if (typeof field !== 'undefined') {
        if(!field.$dirty) { field.$touch(); }
      }
    },
    recaptcha() {
      this.$recaptcha("login").then(token => {
        this.recaptchaToken = token;
      });
    },
    submit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.sending = true;
        this.sendMessage().then(() => {
          this.sent = true;
          this.sending = false;
        }).catch((e)=>{
          if(e.response && e.response.status === 429) {
            this.tooManyRequestsError = true;
          } else {
            this.errorSending = true;
          }
          this.sending = false;
          console.log('Error sending message', err);
        });
      }
    },
    sendMessage() {
      const params = {
        text: this.form.message,
        from: this.form.name,
        site_key: this.siteKey,
        callback_number: this.form.phone,
        targets: [
          {
            id: this.targetId,
            type: this.targetType,
            alias_id: this.aliasId,
          }
        ],
        recaptcha_token: this.recaptchaToken,
      };

      return this.$axios.post('/api/messages', params);
    },
    getSiteLogo() {
      this.$axios.get(`/api/sites/${this.siteKey}`).then(({ data }) => {
        this.logoUrl = data.logo_url;
      }).catch((e) => {
        // Will be handled by rollbar
        if(e.response && e.response.status === 429) {
          this.tooManyRequestsError = true;
        }
        console.log('Error getting site logo', e);
      });
    },
    getTarget() {
      this.$axios.get(`/api/sites/${this.siteKey}/targets/${this.id}`).then(({ data }) => {
        this.displayName = data[0].display_name;
        this.targetId = data[0].id;
        this.targetType = data[0].type;
        this.aliasId = data[0].alias_id;
      }).catch((e) => {
        if(e.response && e.response.status === 429) {
          this.tooManyRequestsError = true;
        } else {
          this.notFound = true;
        }
        // Will be handled by rollbar
        console.log('Error getting target', e);
      });
    },
    navigateHome() {
      if(_.get(this.fromRoute, 'name', '') === 'home') {
        this.$router.push({ name: 'home', params: { siteKey: this.siteKey }});
      } else {
        this.$router.go();
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.fromRoute = from;
    });
  },
  created() {
    this.getSiteLogo();
    this.getTarget();
  }
};
</script>

<style lang="scss" scoped>
@media (min-width: 1000px) {
  .pad-lg {
    padding-top: 40px;
  }
}
.app-container {
  height: 0;
}
.loading {
  span {
    margin-left: 20px;
  }
}
.error-sending {
  padding-top: 20px;
  display: flex;
  justify-content: center;
}
.md-invalid, .md-error {
  color: red;
}
.message-textarea {
  min-height: 70px;
}
.captcha {
  background-color: #eee;
  border: solid 1px rgba(0, 0, 0, 0.1);
  padding: 7px 10px;
  input {
    margin-right: 6px;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    cursor: pointer;
  }
}
.center-container {
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
  height: 100%;
  display: flex;
  .home-link {
    color: #71277a;
    cursor: pointer;
  }
}
</style>
